import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import { useMutation } from '@apollo/client';
import { useParams, useHistory } from 'react-router-dom';
import { ValidatorForm } from 'react-form-validator-core';

import useErrorHandlers from 'core/hooks/useErrorHandlers';
import { myProgramPath } from 'onramp/api/navigation';

import {
  Box,
  Button,
  Container,
  Flex,
  FormGroup,
  Hr,
  List,
  Header,
  ValidatedInput,
} from 'core/ui/atoms';
import { RESET_PASSWORD } from 'core/api/apollo/mutations';
import { ERROR_MESSAGES } from 'core/api/constants';
import { CURRENT_USER } from 'core/api/apollo/queries';
import cacheUpdater from 'core/api/cache/updaters';

const minPwdLength = 8;
const maxPwdLength = 128;

function ResetPasswordPage() {
  const { token } = useParams();
  const history = useHistory();

  const currentUserRef = useRef(null);

  const formDefaults = { password: '', passwordConfirmation: '' };
  const [formData, setFormData] = useState(formDefaults);
  const [isProcessing, setIsProcessing] = useState(false);
  const { displayApolloResponseErrors, handleGenericApolloError } = useErrorHandlers();

  ValidatorForm.addValidationRule('isPasswordMatch', (value) => value === formData.password);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const resetForm = () => {
    setIsProcessing(false);
    setFormData(formDefaults);
  };

  const onError = (error) => {
    setIsProcessing(false);
    handleGenericApolloError(error);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setIsProcessing(true);
    resetPassword().catch(resetForm);
  };

  const onComplete = ({ resetPassword: { errors } }) => {
    setIsProcessing(false);

    if (errors && errors.length) {
      displayApolloResponseErrors(errors);
      resetForm();
      return;
    } else {
      const currentUser = currentUserRef.current;
      if (isEmpty(currentUser?.subscription?.subscriptionQuantities)) {
        history.push(myProgramPath());
      } else {
        history.push('/');
      }
    }
  };

  const updateCache = cacheUpdater('resetPassword', (cache, { user }) => {
    const { currentUser } = cache.readQuery({ query: CURRENT_USER });
    currentUserRef.current = { ...currentUser, ...user };
    cache.writeQuery({
      query: CURRENT_USER,
      data: { currentUser: currentUserRef.current },
    });
  });

  const [resetPassword] = useMutation(RESET_PASSWORD, {
    variables: { input: { token, ...formData } },
    onError: onError,
    onCompleted: onComplete,
    update: updateCache,
  });

  return (
    <Container>
      <Flex alignItems='center' justifyContent='center'>
        <FullWidthContainer>
          <WelcomeHeader>Create New Password</WelcomeHeader>
          <Hr />
          <Form
            onSubmit={onSubmit}
            noValidate
            instantValidate={false}
          >
            <CustomFormGroup>
              <ValidatedInputText
                type="password"
                name="password"
                onChange={handleChange}
                value={formData.password}
                placeholder="New Password"
                validators={['required',
                  `minStringLength:${minPwdLength}`,
                  `maxStringLength:${maxPwdLength}`,
                ]}
                errorMessages={[
                  ERROR_MESSAGES.required,
                  ERROR_MESSAGES.passwordLength,
                  ERROR_MESSAGES.passwordLength,
                ]}
              />
            </CustomFormGroup>
            <CustomFormGroup>
              <ValidatedInputText
                type="password"
                name="passwordConfirmation"
                placeholder="Confirm Password"
                onChange={handleChange}
                value={formData.passwordConfirmation}
                validators={['required', 'isPasswordMatch']}
                errorMessages={[ERROR_MESSAGES.required, ERROR_MESSAGES.passwordMatch]}
              />
            </CustomFormGroup>
            <Ul>
              <ListItem>Use at least 8 characters</ListItem>
              <ListItem>Include a mix of letters, numbers, and symbols</ListItem>
            </Ul>
            <ButtonCont>
              <ButtonFormGroup>
                <SubmitButton type="submit" btn-lg disabled={isProcessing}>
                  {isProcessing ? 'Resetting...' : 'Submit'}
                </SubmitButton>
              </ButtonFormGroup>
            </ButtonCont>
          </Form>
        </FullWidthContainer>
      </Flex>
    </Container>
  );
}

const FullWidthContainer = styled(Box)`
  width: 100%;
`;

const ButtonFormGroup = styled(FormGroup)`
  height: 42px;
  ${(p) => p.theme.max('sm')`
    height: 52px;
  `}
`;

const SubmitButton = styled(Button)`
  border-radius: 4px;
  padding: 12px 24px;
  height: 42px;
  border-color: ${(p) => p.theme.colors.green.light};
  background: ${(p) => p.theme.colors.green.light};
  color: ${(p) => p.theme.colors.primary};
  text-transform: uppercase;
  ${(p) => p.theme.max('md')`
    width: 90% !important;
    color: ${(p) => p.theme.colors.primary};
    text-transform: uppercase;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 10%;
  `}
`;

const Ul = styled.ul`
  font-size: 14px;
  font-weight: 400;
  padding-left: 20px;
  padding-right: 20px;
`;

const ListItem = styled(List.Item)`
  display: list-item;
`;

const CustomFormGroup = styled(FormGroup)`
  margin-bottom: 1vh !important;
`;

const ValidatedInputText = styled(ValidatedInput)`
  border-radius: 4px;
  border: solid 1px #36443A;
  padding: 0.65em 1em;
`;

const WelcomeHeader = styled(Header)`
  margin-top: 1vh;
`;

const Form = styled(ValidatorForm)`
  display: block;
  width: 90%;
  margin: 3vh auto 3vh auto;
  > div:nth-child(1) {
    margin-bottom: 5vh;
  }
  ${(p) => p.theme.min('sm')`
    width: 320px;
    height: 52vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
  `}
  ${(p) => p.theme.max('sm')`
    padding: 0 calc(${(p) => p.theme.layout.mobilePadding} * 2);
  `}
`;

const ButtonCont = styled.div`
  text-align: center;
  margin-top: 8vh;
  border-color: ${(p) => p.theme.colors.green.light};
  background: ${(p) => p.theme.colors.green.light};
  color: ${(p) => p.theme.colors.primary};
  text-transform: uppercase;
  border-radius: 4px;
  ${(p) => p.theme.max('sm')`
    button {
      width: 100%;
      border-color: ${(p) => p.theme.colors.green.light};
      background: ${(p) => p.theme.colors.green.light};
      color: ${(p) => p.theme.colors.primary};
      text-transform: uppercase;
      font-size: 15px;
      font-weight: 600;
      letter-spacing: 10%;
    }
  `}
`;

export default ResetPasswordPage;
